import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, FileText, Minus, Plus, Maximize2, X } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.mjs`;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.1;

// Compact toolbar: zoom −/+ and expand/collapse
const ViewerToolbar = ({ zoom, setZoom, expanded, setExpanded }) => (
  <div className="flex items-center justify-end gap-1 px-2 py-1.5 bg-white/80 backdrop-blur border-b border-slate-200">
    <button
      onClick={() => setZoom(z => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))}
      className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"
      title="Zoom out"
      data-testid="parent-menu-zoom-out"
    >
      <Minus className="w-3.5 h-3.5" />
    </button>
    <span className="text-[11px] font-medium text-slate-500 w-10 text-center" data-testid="parent-menu-zoom-pct">
      {Math.round(zoom * 100)}%
    </span>
    <button
      onClick={() => setZoom(z => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))}
      className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"
      title="Zoom in"
      data-testid="parent-menu-zoom-in"
    >
      <Plus className="w-3.5 h-3.5" />
    </button>
    <button
      onClick={() => setExpanded(v => !v)}
      className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600 ml-1"
      title={expanded ? "Collapse" : "Expand"}
      data-testid="parent-menu-toggle-expand"
    >
      {expanded ? <X className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
    </button>
  </div>
);

// PDF — read-only inline canvas render, auto-fits width, zoomable
const PDFInline = ({ fileUrl, filename, zoom, baseWidth }) => {
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  if (error) {
    return (
      <div className="rounded-xl bg-white border border-slate-200 p-6 text-center" data-testid="parent-menu-pdf-error">
        <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500">Unable to display {filename}</p>
      </div>
    );
  }
  return (
    <div className="bg-slate-100" data-testid="parent-menu-pdf">
      <div className="overflow-auto p-3">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
            <span className="ml-2 text-sm text-slate-500">Loading menu…</span>
          </div>
        )}
        <Document
          file={fileUrl}
          onLoadSuccess={({ numPages }) => { setNumPages(numPages); setLoading(false); }}
          onLoadError={() => { setError(true); setLoading(false); }}
          loading=""
        >
          <div className="flex flex-col items-center gap-3">
            {Array.from(new Array(numPages || 0), (_, i) => (
              <Page
                key={`p_${i + 1}`}
                pageNumber={i + 1}
                width={baseWidth > 0 ? baseWidth * zoom : undefined}
                className="shadow-md"
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            ))}
          </div>
        </Document>
      </div>
    </div>
  );
};

// Excel — fetch /excel-data and render read-only HTML table, font scales with zoom
const ExcelInline = ({ month, menuType, token, zoom }) => {
  const [rows, setRows] = useState([]);
  const [merges, setMerges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${API}/daily-menu/${month}/excel-data?token=${encodeURIComponent(token || "")}&menu_type=${menuType || "lunch"}`
        );
        if (!cancelled) {
          setRows(res.data.rows || []);
          setMerges(res.data.merges || []);
        }
      } catch {
        if (!cancelled) setError("Could not load menu");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [month, menuType, token]);

  const { mergeMap, skipSet } = useMemo(() => {
    const mm = {};
    const ss = new Set();
    merges.forEach(m => {
      mm[`${m.minRow - 1},${m.minCol - 1}`] = {
        rowSpan: m.maxRow - m.minRow + 1,
        colSpan: m.maxCol - m.minCol + 1,
      };
      for (let r = m.minRow - 1; r <= m.maxRow - 1; r++) {
        for (let c = m.minCol - 1; c <= m.maxCol - 1; c++) {
          if (r !== m.minRow - 1 || c !== m.minCol - 1) ss.add(`${r},${c}`);
        }
      }
    });
    return { mergeMap: mm, skipSet: ss };
  }, [merges]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 bg-white" data-testid="parent-menu-excel-loading">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
        <span className="ml-2 text-sm text-slate-500">Loading menu…</span>
      </div>
    );
  }
  if (error || rows.length === 0) {
    return (
      <div className="bg-white p-6 text-center" data-testid="parent-menu-excel-error">
        <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500">{error || "Menu is empty"}</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto bg-white" data-testid="parent-menu-excel">
      <table className="w-full border-collapse" style={{ fontSize: `${zoom * 0.85}rem` }}>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => {
                if (skipSet.has(`${r},${c}`)) return null;
                const span = mergeMap[`${r},${c}`] || {};
                const v = (cell && typeof cell === "object") ? cell.v : cell;
                const styleObj = (cell && typeof cell === "object" && cell.style) ? cell.style : {};
                return (
                  <td
                    key={c}
                    rowSpan={span.rowSpan || 1}
                    colSpan={span.colSpan || 1}
                    className="border border-slate-200 px-2 py-1 align-top"
                    style={styleObj}
                  >
                    {v != null ? String(v) : ""}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Image — direct <img>, scales with zoom
const ImageInline = ({ fileUrl, filename, zoom }) => (
  <div className="bg-white flex justify-center overflow-auto" data-testid="parent-menu-image">
    <img
      src={fileUrl}
      alt={filename || "Menu"}
      className="h-auto"
      style={{ width: `${zoom * 100}%`, maxWidth: 'none' }}
    />
  </div>
);

// Main component — toolbar + renderer; supports inline + expanded (breaks out of card)
const ParentMenuViewer = ({ fileType, fileUrl, filename, month, menuType, hasPdf }) => {
  const ft = (fileType || "").toLowerCase();
  const token = localStorage.getItem("token") || localStorage.getItem("access_token") || "";
  const [zoom, setZoom] = useState(1.0);
  const [expanded, setExpanded] = useState(false);
  const [baseWidth, setBaseWidth] = useState(0);
  const containerRef = React.useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const update = () => setBaseWidth(Math.max(0, el.clientWidth - 24));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [expanded]);

  const renderBody = () => {
    if (ft === "pdf" || hasPdf) {
      return <PDFInline fileUrl={fileUrl} filename={filename} zoom={zoom} baseWidth={baseWidth} />;
    }
    if (ft === "xlsx" || ft === "xls") {
      return <ExcelInline month={month} menuType={menuType} token={token} zoom={zoom} />;
    }
    if (["png", "jpg", "jpeg", "gif", "webp"].includes(ft)) {
      return <ImageInline fileUrl={fileUrl} filename={filename} zoom={zoom} />;
    }
    return (
      <div className="bg-white p-6 text-center" data-testid="parent-menu-unknown">
        <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500">Menu format not supported for inline viewing</p>
      </div>
    );
  };

  // When expanded, break out of the green card column and float at ~90% viewport width
  const wrapperCls = expanded
    ? "fixed left-1/2 top-4 -translate-x-1/2 z-40 w-[92vw] max-w-[1400px] max-h-[92vh] overflow-auto rounded-2xl border border-slate-200 bg-white shadow-2xl"
    : "rounded-xl overflow-hidden border border-slate-200 bg-white";

  return (
    <>
      {expanded && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/40 backdrop-blur-sm"
          onClick={() => setExpanded(false)}
          data-testid="parent-menu-backdrop"
        />
      )}
      <div ref={containerRef} className={wrapperCls} data-testid="parent-menu-viewer">
        <ViewerToolbar zoom={zoom} setZoom={setZoom} expanded={expanded} setExpanded={setExpanded} />
        {renderBody()}
      </div>
    </>
  );
};

export default ParentMenuViewer;
